import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { uk, type Dictionary } from './uk';
import { en } from './en';
import { pl } from './pl';

export type Locale = 'uk' | 'en' | 'pl';

/** Flip `enabled` when a translation is complete. */
export const LOCALES: { id: Locale; short: string; enabled: boolean; htmlLang: string; intl: string }[] = [
  { id: 'uk', short: 'UA', enabled: true, htmlLang: 'uk', intl: 'uk-UA' },
  { id: 'en', short: 'EN', enabled: false, htmlLang: 'en', intl: 'en-GB' },
  { id: 'pl', short: 'PL', enabled: false, htmlLang: 'pl', intl: 'pl-PL' },
];

const OVERRIDES = { uk: {}, en, pl } as const;

function deepMerge<T>(base: T, over: unknown): T {
  if (over == null || typeof over !== 'object' || Array.isArray(over)) return (over ?? base) as T;
  const out: any = Array.isArray(base) ? [...(base as any)] : { ...(base as any) };
  for (const [k, v] of Object.entries(over as object)) {
    out[k] = v !== null && typeof v === 'object' && !Array.isArray(v) ? deepMerge((base as any)[k], v) : v;
  }
  return out;
}

/** Replaces {name} placeholders. */
export function fmt(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`));
}

interface I18nValue {
  locale: Locale;
  intl: string;
  d: Dictionary;
  setLocale: (l: Locale) => void;
  nf: (n: number, opts?: Intl.NumberFormatOptions) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children, initial = 'uk' }: { children: ReactNode; initial?: Locale }) {
  const [locale, setLocaleState] = useState<Locale>(initial);
  const value = useMemo<I18nValue>(() => {
    const cfg = LOCALES.find((l) => l.id === locale) ?? LOCALES[0];
    const d = deepMerge(uk as Dictionary, OVERRIDES[locale]);
    return {
      locale,
      intl: cfg.intl,
      d,
      setLocale: (l) => {
        const next = LOCALES.find((x) => x.id === l);
        if (!next?.enabled) return;
        document.documentElement.lang = next.htmlLang;
        setLocaleState(l);
      },
      nf: (n, opts) => new Intl.NumberFormat(cfg.intl, opts).format(n),
    };
  }, [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const v = useContext(I18nContext);
  if (!v) throw new Error('useI18n outside I18nProvider');
  return v;
}
